<template>
  <div class="pageContainer">
    <div
      v-if="!loading"
      class="contentContainer grow"
      style="margin-bottom: 16px"
    >
      <CustomerLastNameField
        field-id="lastName"
        :required="false"
        :checkValidity="false"
      />

      <CustomerFirstNameField
        field-id="firstName"
        :required="false"
        :checkValidity="false"
      />

      <div
        v-for="question in questions"
        :key="question.id"
        class="flex-column align-items-start stretch"
      >
        <!--Simple fields-->
        <transition name="fade" mode="out-in">
          <div class="flex-column gap8 stretch">
            <!-- Input with validation -->

            <!-- Input without Validation -->

            <div
              v-if="
                question.fieldType !== SPECIAL_FIELD_TYPE.BIRTH_DATE &&
                simpleFieldGetter(question.fieldId).type ===
                SIMPLE_FIELD_TYPE.INPUT
              "
              class="inputField"
            >
              <label class="textBold" :for="question.fieldId">
                {{ simpleFieldGetter(question.fieldId).label }}
              </label>
              <input
                :id="question.fieldId"
                class="textInput"
                :value="
                  readAnswer(
                    simpleFieldGetter(question.fieldId).identifier,
                    simpleFieldGetter(question.fieldId).mask
                  )
                "
                @blur="
                  writeAnswer(
                    $event.currentTarget.value,
                    simpleFieldGetter(question.fieldId).identifier
                  )
                "
              />
            </div>

            <!--Select-->

            <SelectWithValidation
              v-if="
                question.fieldType !== SPECIAL_FIELD_TYPE.BIRTH_DATE &&
                simpleFieldGetter(question.fieldId).type ===
                SIMPLE_FIELD_TYPE.SELECT
              "
              :id="question.fieldId"
              :label="simpleFieldGetter(question.fieldId).label"
              :is-required="true"
              :options="simpleFieldGetter(question.fieldId).inputValues"
              :value="
                readAnswer(simpleFieldGetter(question.fieldId).identifier)
              "
              @input="
                writeAnswer(
                  $event,
                  simpleFieldGetter(question.fieldId).identifier
                )
              "
            />

            <!--Date picker-->
            <div
              v-if="
                question.fieldType !== SPECIAL_FIELD_TYPE.BIRTH_DATE &&
                simpleFieldGetter(question.fieldId).type ===
                SIMPLE_FIELD_TYPE.DATE_PICKER
              "
              class="inputField"
            >
              <label class="textBold" :for="question.fieldId">
                {{ simpleFieldGetter(question.fieldId).label }}
                <span
                  v-if="question.isRequired"
                  class="bold"
                  style="color: var(--systemError)"
                  >*</span
                >
              </label>
              <InputCalendar
                :default-value="
                  readAnswer(simpleFieldGetter(question.fieldId).identifier)
                    ? new Date(
                        readAnswer(
                          simpleFieldGetter(question.fieldId).identifier
                        )
                      )
                    : null
                "
                @input="
                  writeAnswer($event, {
                    identifier: simpleFieldGetter(question.fieldId).identifier,
                    date: true,
                  })
                "
              />
            </div>

            <!--Radio-->
            <ApplicationRadioQuestion
              v-if="
                question.fieldType !== SPECIAL_FIELD_TYPE.BIRTH_DATE &&
                simpleFieldGetter(question.fieldId).type ===
                SIMPLE_FIELD_TYPE.RADIO
              "
              :field="simpleFieldGetter(question.fieldId)"
              :is-required="question.isRequired"
              :value="
                readAnswer(simpleFieldGetter(question.fieldId).identifier)
              "
              @input="
                writeAnswer(
                  $event,
                  simpleFieldGetter(question.fieldId).identifier
                )
              "
            />

            <!--Checkbox-->
            <ApplicationCheckboxQuestion
              v-if="
                question.fieldType !== SPECIAL_FIELD_TYPE.BIRTH_DATE &&
                simpleFieldGetter(question.fieldId).type ===
                SIMPLE_FIELD_TYPE.CHECKBOX
              "
              :field="simpleFieldGetter(question.fieldId)"
              :is-required="question.isRequired"
              :value="
                readAnswer(simpleFieldGetter(question.fieldId).identifier)
              "
              @input="
                writeAnswer(
                  $event,
                  simpleFieldGetter(question.fieldId).identifier
                )
              "
            />

            <CustomerBirthDateField
              v-if="
                question.fieldType === SPECIAL_FIELD_TYPE.BIRTH_DATE &&
                specialFieldGetter(question.fieldId).type === SPECIAL_FIELD_TYPE.BIRTH_DATE
              "
              :field-id="question.fieldId"
              :required="question.isRequired"
              :checkValidity="hasMissingFields"
            />
          </div>
        </transition>
      </div>
    </div>
    <div class="actionArea">
      <button
        class="linkButton linkButtonSuccess justify-self-start-desktop leftAction"
        @click="previousStep"
      >
        <font-awesome-icon :icon="['fas', 'arrow-left']" />{{
          $t("bnpl:cancel")
        }}
      </button>
      <button class="btn btnL rightAction" @click="nextStep">
        {{ $t("bnpl:continue") }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { useLimitStore } from "~~/stores/limit";
import { useSimpleFieldStore } from "~~/stores/simpleField";
import { useSpecialFieldStore } from "~~/stores/specialField";
import { SIMPLE_FIELD_TYPE, SPECIAL_FIELD_TYPE } from "~~/utils/const";

const limitStore = useLimitStore();
const simpleFieldStore = useSimpleFieldStore();
const specialFieldStore = useSpecialFieldStore();

const loading = ref(true);

const questions = ref([]);

const dynamicData = computed(() => {
  if (limitStore.limit.dynamicData) {
    return JSON.parse(limitStore.limit.dynamicData);
  }
  return {};
});

onBeforeMount(async () => {
  await limitStore.getScoringQuestions();
  questions.value = limitStore.scoringQuestions;

  if (questions.value.length <= 0) {
    limitStore.nextPosition();
  }

  await fetchSimpleFields();
  await fetchSpecialFields();
  loading.value = false;
});

function getRules(question) {
  let rules = { required: question.isRequired };
  let regex = simpleFieldGetter(question.fieldId).validation;
  if (regex) {
    rules["regex"] = regex;
  }
  return rules;
}

function readAnswer(identifier, mask) {
  if (dynamicData.value[identifier]) {
    return dynamicData.value[identifier];
  }
  return null;
}

function writeAnswer(value, identifier) {
  if (typeof identifier !== "string" && identifier.date) {
    value = formatDate(value);
    identifier = identifier.identifier;
  }

  let newDynamicData = {
    ...dynamicData.value,
  };

  newDynamicData[identifier] = value;
  limitStore.saveDynamicData(newDynamicData);
}

async function fetchSimpleFields() {
  for (let question of questions.value) {
    if (question.fieldType !== SPECIAL_FIELD_TYPE.BIRTH_DATE) {
      await simpleFieldStore.fetchOne(question.fieldId);
    }
  }
}

async function fetchSpecialFields() {
  for (let question of questions.value) {
    if (question.fieldType === SPECIAL_FIELD_TYPE.BIRTH_DATE) {
      await specialFieldStore.fetchOne(question.fieldId);
    }
  }
}

function simpleFieldGetter(id) {
  return simpleFieldStore.fieldData[id];
}

function specialFieldGetter(id) {
  return specialFieldStore.specialFieldData[id];
}

function nextStep() {
  limitStore.nextPosition();
}

function previousStep() {
  limitStore.previousPosition();
}
</script>

<style scoped lang="scss">
</style>
